import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';

// Import match details components
import MatchHeader from './components/MatchHeader';
import AttendanceCard from './components/AttendanceCard';
import PlayerList from './components/PlayerList';
import PaymentSection from './components/PaymentSection';
import MatchChat from './components/MatchChat';
import BarbecueIntegration from './components/BarbecueIntegration';
import MatchStats from './components/MatchStats';
import AdminControls from './components/AdminControls';
import WeatherInfo from './components/WeatherInfo';

const MatchDetails = () => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const [matchData, setMatchData] = useState(null);
  const [userAttendance, setUserAttendance] = useState('pending');
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [isLoading, setIsLoading] = useState(true);

  // Mock data initialization
  useEffect(() => {
    const loadMatchDetails = () => {
      // Mock match data
      const mockMatch = {
        id: matchId || 1,
        title: 'Pelada de Sábado',
        description: 'Racha tradicional do final de semana com a galera. Venha jogar e se divertir!',
        venue: {
          name: 'Campo do Morumbi',
          address: 'Rua das Palmeiras, 123 - Morumbi, São Paulo - SP',
          coordinates: { lat: -23.5986, lng: -46.6731 },
          facilities: ['Vestiário', 'Estacionamento', 'Bar', 'Arquibancada'],
          rating: 4.5,
          image: "https://images.unsplash.com/photo-1661267237969-617c958c8319",
          imageAlt: 'Green soccer field with white goal posts and clear markings surrounded by trees'
        },
        date: '2025-10-18',
        time: '14:00',
        duration: 120,
        status: 'confirmed',
        maxPlayers: 22,
        pricePerPlayer: 25.00,
        creator: {
          id: 'user1',
          name: 'João Silva',
          avatar: "https://images.unsplash.com/photo-1681398836231-d0b89bd571d6",
          avatarAlt: 'Professional headshot of young man with short brown hair in casual shirt',
          reputation: 4.8
        },
        weather: {
          temperature: 24,
          condition: 'Parcialmente nublado',
          icon: 'Cloud',
          rainChance: 20,
          windSpeed: 8,
          humidity: 65,
          forecast: 'Clima ideal para futebol'
        },
        confirmedPlayers: [
        {
          id: 'user1',
          name: 'João Silva',
          avatar: "https://images.unsplash.com/photo-1681398836231-d0b89bd571d6",
          avatarAlt: 'Professional headshot of young man with short brown hair in casual shirt',
          reputation: 4.8,
          attendanceHistory: 95,
          position: 'Atacante',
          isPaid: true,
          confirmedAt: new Date(Date.now() - 86400000)
        },
        {
          id: 'user2',
          name: 'Pedro Santos',
          avatar: "https://images.unsplash.com/photo-1663720527180-4c60a78fe3b7",
          avatarAlt: 'Professional headshot of man with beard in blue shirt',
          reputation: 4.3,
          attendanceHistory: 87,
          position: 'Meio-campo',
          isPaid: true,
          confirmedAt: new Date(Date.now() - 43200000)
        },
        {
          id: 'user3',
          name: 'Carlos Lima',
          avatar: 'https://images.unsplash.com/photo-1723607528434-21cde67167c4',
          avatarAlt: 'Professional headshot of man with glasses in white shirt',
          reputation: 4.1,
          attendanceHistory: 78,
          position: 'Zagueiro',
          isPaid: false,
          confirmedAt: new Date(Date.now() - 21600000)
        },
        {
          id: 'user4',
          name: 'Rafael Oliveira',
          avatar: 'https://images.unsplash.com/photo-1706823906110-40052599e7d3',
          avatarAlt: 'Professional headshot of young man with curly hair in casual wear',
          reputation: 4.6,
          attendanceHistory: 91,
          position: 'Goleiro',
          isPaid: true,
          confirmedAt: new Date(Date.now() - 10800000)
        },
        {
          id: 'user5',
          name: 'Ana Costa',
          avatar: 'https://images.unsplash.com/photo-1628013663808-25ef6a3b28b4',
          avatarAlt: 'Professional headshot of woman with long brown hair in business attire',
          reputation: 4.4,
          attendanceHistory: 89,
          position: 'Meio-campo',
          isPaid: true,
          confirmedAt: new Date(Date.now() - 7200000)
        },
        {
          id: 'user6',
          name: 'Lucas Ferreira',
          avatar: 'https://images.unsplash.com/photo-1663720527180-4c60a78fe3b7',
          avatarAlt: 'Professional headshot of man with short dark hair in suit',
          reputation: 4.2,
          attendanceHistory: 82,
          position: 'Lateral',
          isPaid: false,
          confirmedAt: new Date(Date.now() - 3600000)
        }],

        pendingPlayers: [
        {
          id: 'user7',
          name: 'Marcos Alves',
          avatar: 'https://images.unsplash.com/photo-1667575949231-fbf430640797',
          avatarAlt: 'Professional headshot of man with styled hair in casual shirt',
          reputation: 4.0,
          attendanceHistory: 75,
          position: 'Atacante',
          invitedAt: new Date(Date.now() - 1800000)
        },
        {
          id: 'user8',
          name: 'Felipe Costa',
          avatar: 'https://images.unsplash.com/photo-1706823906110-40052599e7d3',
          avatarAlt: 'Professional headshot of young man with curly hair in casual wear',
          reputation: 4.3,
          attendanceHistory: 84,
          position: 'Meio-campo',
          invitedAt: new Date(Date.now() - 900000)
        }],

        declinedPlayers: [
        {
          id: 'user9',
          name: 'Roberto Silva',
          avatar: 'https://images.unsplash.com/photo-1723607528434-21cde67167c4',
          avatarAlt: 'Professional headshot of man with glasses in white shirt',
          reputation: 4.1,
          attendanceHistory: 79,
          position: 'Zagueiro',
          declinedAt: new Date(Date.now() - 14400000),
          declineReason: 'Compromisso familiar'
        }],

        paymentInfo: {
          totalAmount: 550.00,
          paidAmount: 350.00,
          pendingAmount: 200.00,
          pixKey: '11999887766',
          bankDetails: {
            bank: 'Nubank',
            account: 'João Silva'
          }
        },
        barbecue: {
          id: 'bbq1',
          title: 'Churrasco pós-pelada',
          location: 'Campo do Morumbi - Área de Lazer',
          time: '16:30',
          costPerPerson: 35.00,
          organizer: 'Carlos Lima',
          participants: 8,
          menu: ['Picanha', 'Linguiça', 'Frango', 'Saladas', 'Bebidas'],
          isActive: true
        },
        statistics: {
          totalMatches: 12,
          regularPlayers: 8,
          averageAttendance: 18,
          averageRating: 4.3,
          lastMatchScore: '3x2',
          winRate: 67
        },
        chatMessages: [
        {
          id: 1,
          type: 'message',
          user: {
            name: 'João Silva',
            avatar: 'https://images.unsplash.com/photo-1641479160067-5ae7bde244b0',
            avatarAlt: 'Professional headshot of young man with short brown hair in casual shirt'
          },
          content: 'Pessoal, confirmem presença até amanhã!',
          timestamp: new Date(Date.now() - 3600000)
        },
        {
          id: 2,
          type: 'system',
          content: 'Pedro Santos confirmou presença',
          timestamp: new Date(Date.now() - 1800000)
        },
        {
          id: 3,
          type: 'message',
          user: {
            name: 'Carlos Lima',
            avatar: 'https://images.unsplash.com/photo-1723607528434-21cde67167c4',
            avatarAlt: 'Professional headshot of man with glasses in white shirt'
          },
          content: 'Organizei um churrasco depois do jogo, quem topa?',
          timestamp: new Date(Date.now() - 900000)
        },
        {
          id: 4,
          type: 'weather',
          content: 'Previsão atualizada: 20% de chance de chuva',
          timestamp: new Date(Date.now() - 600000)
        }]

      };

      setMatchData(mockMatch);
      setIsAdmin(mockMatch?.creator?.id === 'user1'); // Simulate current user as admin
      setUserAttendance('confirmed'); // Simulate user attendance status
      setIsLoading(false);
    };

    // Simulate loading delay
    const timer = setTimeout(loadMatchDetails, 800);
    return () => clearTimeout(timer);
  }, [matchId]);

  const handleAttendanceChange = (status) => {
    setUserAttendance(status);

    // Update match data accordingly
    if (matchData) {
      const updatedMatch = { ...matchData };
      const currentUser = {
        id: 'currentUser',
        name: 'Você',
        avatar: 'https://images.unsplash.com/photo-1641479160067-5ae7bde244b0',
        avatarAlt: 'Your profile avatar',
        reputation: 4.5,
        attendanceHistory: 92,
        position: 'Meio-campo'
      };

      // Remove user from all lists first
      updatedMatch.confirmedPlayers = updatedMatch?.confirmedPlayers?.filter((p) => p?.id !== 'currentUser');
      updatedMatch.pendingPlayers = updatedMatch?.pendingPlayers?.filter((p) => p?.id !== 'currentUser');
      updatedMatch.declinedPlayers = updatedMatch?.declinedPlayers?.filter((p) => p?.id !== 'currentUser');

      // Add to appropriate list based on status
      switch (status) {
        case 'confirmed':
          updatedMatch?.confirmedPlayers?.push({
            ...currentUser,
            isPaid: false,
            confirmedAt: new Date()
          });
          break;
        case 'declined':
          updatedMatch?.declinedPlayers?.push({
            ...currentUser,
            declinedAt: new Date(),
            declineReason: 'Não posso comparecer'
          });
          break;
        case 'pending':
          updatedMatch?.pendingPlayers?.push({
            ...currentUser,
            invitedAt: new Date()
          });
          break;
      }

      setMatchData(updatedMatch);
    }
  };

  const handleShare = () => {
    if (navigator?.share && matchData) {
      navigator.share({
        title: matchData?.title,
        text: `Participe da ${matchData?.title} no ${matchData?.venue?.name}`,
        url: window.location?.href
      });
    } else {
      // Fallback to clipboard
      navigator?.clipboard?.writeText(window.location?.href);
      // Show toast notification (implementation depends on your toast system)
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-64 bg-muted rounded-lg"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-48 bg-muted rounded-lg"></div>
                <div className="h-64 bg-muted rounded-lg"></div>
              </div>
              <div className="space-y-6">
                <div className="h-32 bg-muted rounded-lg"></div>
                <div className="h-48 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  if (!matchData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <Icon name="AlertCircle" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
              Pelada não encontrada
            </h1>
            <p className="text-muted-foreground mb-6">
              A pelada que você está procurando não existe ou foi removida.
            </p>
            <Button onClick={() => navigate('/dashboard')} iconName="ArrowLeft" iconPosition="left">
              Voltar ao Dashboard
            </Button>
          </div>
        </div>
      </div>);

  }

  const formatDate = (dateString) => {
    const date = parseISO(dateString);
    if (isToday(date)) return 'Hoje';
    if (isTomorrow(date)) return 'Amanhã';
    return format(date, "EEEE, dd 'de' MMMM", { locale: ptBR });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6">
        {/* Back Navigation */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            iconName="ArrowLeft"
            iconPosition="left"
            className="text-muted-foreground hover:text-foreground">

            Voltar
          </Button>
        </div>

        {/* Match Header */}
        <MatchHeader
          match={matchData}
          onShare={handleShare}
          formatDate={formatDate} />


        {/* Mobile Tabs */}
        <div className="lg:hidden mb-6">
          <div className="flex space-x-1 bg-muted p-1 rounded-lg">
            {[
            { id: 'details', label: 'Detalhes', icon: 'Info' },
            { id: 'players', label: 'Jogadores', icon: 'Users' },
            { id: 'chat', label: 'Chat', icon: 'MessageCircle' }]?.
            map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab?.id ?
              'bg-background text-foreground shadow-sm' :
              'text-muted-foreground hover:text-foreground'}`
              }>

                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Attendance Card - Always Visible */}
            <AttendanceCard
              userAttendance={userAttendance}
              onAttendanceChange={handleAttendanceChange}
              matchData={matchData} />


            {/* Conditional Content Based on Active Tab (Mobile) or Always Show (Desktop) */}
            <div className={`space-y-6 ${activeTab !== 'details' ? 'hidden lg:block' : ''}`}>
              {/* Weather Info */}
              <WeatherInfo weather={matchData?.weather} />

              {/* Payment Section */}
              <PaymentSection
                paymentInfo={matchData?.paymentInfo}
                userAttendance={userAttendance}
                isAdmin={isAdmin} />


              {/* Barbecue Integration */}
              {matchData?.barbecue?.isActive &&
              <BarbecueIntegration barbecue={matchData?.barbecue} />
              }

              {/* Match Statistics */}
              <MatchStats statistics={matchData?.statistics} />

              {/* Admin Controls */}
              {isAdmin &&
              <AdminControls
                matchData={matchData}
                onUpdateMatch={setMatchData} />

              }
            </div>

            {/* Player List - Mobile Tab Content */}
            <div className={`${activeTab !== 'players' ? 'hidden lg:block' : ''}`}>
              <PlayerList
                confirmedPlayers={matchData?.confirmedPlayers}
                pendingPlayers={matchData?.pendingPlayers}
                declinedPlayers={matchData?.declinedPlayers}
                maxPlayers={matchData?.maxPlayers}
                isAdmin={isAdmin} />

            </div>

            {/* Match Chat - Mobile Tab Content */}
            <div className={`${activeTab !== 'chat' ? 'hidden lg:block' : ''}`}>
              <MatchChat
                messages={matchData?.chatMessages}
                matchId={matchData?.id}
                onSendMessage={(message) => {
                  const newMessage = {
                    id: Date.now(),
                    type: 'message',
                    user: {
                      name: 'Você',
                      avatar: 'https://images.unsplash.com/photo-1641479160067-5ae7bde244b0',
                      avatarAlt: 'Your profile avatar'
                    },
                    content: message,
                    timestamp: new Date()
                  };
                  setMatchData((prev) => ({
                    ...prev,
                    chatMessages: [...(prev?.chatMessages || []), newMessage]
                  }));
                }} />

            </div>
          </div>

          {/* Right Column - Sidebar (Desktop Only) */}
          <div className="hidden lg:block space-y-6">
            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
              <h3 className="font-heading font-semibold text-card-foreground mb-4">
                Ações Rápidas
              </h3>
              <div className="space-y-3">
                <Button
                  fullWidth
                  variant="outline"
                  iconName="Share2"
                  iconPosition="left"
                  onClick={handleShare}>

                  Compartilhar
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  iconName="MapPin"
                  iconPosition="left"
                  onClick={() => window.open(`https://maps.google.com?q=${matchData?.venue?.coordinates?.lat},${matchData?.venue?.coordinates?.lng}`, '_blank')}>

                  Ver no Mapa
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => {
                    // Create calendar event
                    const event = {
                      title: matchData?.title,
                      start: new Date(`${matchData?.date}T${matchData?.time}`),
                      end: new Date(`${matchData?.date}T${matchData?.time}`)?.getTime() + matchData?.duration * 60000,
                      location: matchData?.venue?.address
                    };
                    // Implementation depends on calendar integration
                  }}>

                  Adicionar ao Calendário
                </Button>
              </div>
            </div>

            {/* Venue Details */}
            <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
              <div className="relative h-32">
                <AppImage
                  src={matchData?.venue?.image}
                  alt={matchData?.venue?.imageAlt}
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="flex items-center justify-between text-white">
                    <h4 className="font-medium">{matchData?.venue?.name}</h4>
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={12} className="text-yellow-400" />
                      <span className="text-sm">{matchData?.venue?.rating}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-start space-x-2 mb-3">
                  <Icon name="MapPin" size={16} className="text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">{matchData?.venue?.address}</p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium text-card-foreground">Facilidades:</p>
                  <div className="flex flex-wrap gap-1">
                    {matchData?.venue?.facilities?.map((facility, index) =>
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-xs text-muted-foreground">

                        {facility}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Match Summary */}
            <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
              <h3 className="font-heading font-semibold text-card-foreground mb-4">
                Resumo da Pelada
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Participantes</span>
                  <span className="font-medium text-card-foreground">
                    {matchData?.confirmedPlayers?.length || 0}/{matchData?.maxPlayers}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Valor por pessoa</span>
                  <span className="font-medium text-card-foreground">
                    R$ {matchData?.pricePerPlayer?.toFixed(2)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Duração</span>
                  <span className="font-medium text-card-foreground">
                    {Math.floor(matchData?.duration / 60)}h {matchData?.duration % 60}min
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  matchData?.status === 'confirmed' ? 'bg-success/10 text-success' :
                  matchData?.status === 'pending' ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'}`
                  }>
                    {matchData?.status === 'confirmed' && 'Confirmada'}
                    {matchData?.status === 'pending' && 'Pendente'}
                    {matchData?.status === 'cancelled' && 'Cancelada'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default MatchDetails;